
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { YoungSchemasResult } from '@/types/personality';
import { getSchemaDescriptions } from '@/utils/youngSchemasScoring';
import { fadeInVariants } from '@/lib/animations';

interface YoungSchemasRecommendationsProps {
  recommendations: Array<{
    title: string;
    description: string;
    type: 'article' | 'exercise' | 'therapy';
    schema: string;
  }>;
  results: YoungSchemasResult;
}

export default function YoungSchemasRecommendations({ 
  recommendations, 
  results 
}: YoungSchemasRecommendationsProps) {
  return (
    <motion.div
      variants={fadeInVariants}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Recomendações Personalizadas</h2>
        <p className="text-gray-600">
          Baseado nos seus resultados, desenvolvemos recomendações para ajudar
          você a trabalhar com seus esquemas predominantes.
        </p>
      </div>
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {recommendations.map((item, index) => {
          const schemaInfo = item.schema !== 'general' 
            ? getSchemaDescriptions(item.schema)
            : { title: 'Recomendação Geral', description: '' };
          
          return (
            <motion.div
              key={`${item.title}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card className="h-full flex flex-col">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {item.type === 'article' && <span className="text-blue-500 text-xl">📚</span>}
                    {item.type === 'exercise' && <span className="text-green-500 text-xl">🏋️</span>}
                    {item.type === 'therapy' && <span className="text-purple-500 text-xl">🧠</span>}
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                      {item.type === 'article' && 'Artigo'}
                      {item.type === 'exercise' && 'Exercício'}
                      {item.type === 'therapy' && 'Terapia'}
                    </span>
                  </div>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>
                    {item.schema !== 'general' ? `Para o esquema: ${schemaInfo.title}` : 'Para todos os esquemas'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">
                    {item.type === 'article' && 'Ler Artigo'}
                    {item.type === 'exercise' && 'Ver Exercício'}
                    {item.type === 'therapy' && 'Explorar Terapia'}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          );
        })}
      </div>
      
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800">Próximos Passos</CardTitle>
        </CardHeader>
        <CardContent>
          <p>
            Trabalhar com esquemas é um processo contínuo. Considere as seguintes opções para um trabalho mais profundo:
          </p>
          <ul className="list-disc list-inside mt-4 space-y-2">
            <li>Procure um terapeuta especializado em Terapia do Esquema</li>
            <li>Explore livros sobre o assunto, como "Terapia Cognitiva Focada em Esquemas" de Jeffrey Young</li>
            <li>Pratique automonitoramento para identificar quando seus esquemas são disparados</li>
            <li>Utilize técnicas de mindfulness para criar distância entre você e seus esquemas</li>
          </ul>
        </CardContent>
        <CardFooter>
          <p className="text-sm text-blue-700">
            Lembre-se: os esquemas são padrões aprendidos ao longo da vida. Com consciência e prática, é possível modificá-los.
          </p>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
